import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { productsAPI, categoriesAPI } from '../../services/api';
import { 
  ArrowLeft, 
  Package, 
  DollarSign, 
  Tag, 
  Image as ImageIcon, 
  Images,
  Loader2, 
  AlertCircle, 
  Trash2, 
  Check, 
  Info,
  Settings,
  Plus,
  Minus,
  Sparkles,
  Search,
  BarChart3,
  Box,
  ChevronDown,
  Upload,
  X,
  GripVertical
} from 'lucide-react';
import './css/AddProduct.css';

const LONG_DESC_MAX = 2000;
const SHORT_DESC_MIN = 100;
const LONG_DESC_MIN = 1500;

const AddProduct = () => {
  const [formData, setFormData] = useState({
    name: '',
    regularPrice: '',
    salePrice: '',
    categoryId: '',
    shortDescription: '',
    longDescription: '',
    stock: '',
    lowStockThreshold: '10',
    seo_title: '',
    seo_description: '',
    seo_keywords: '',
    options: []
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [newGroupName, setNewGroupName] = useState('');
  const [valueInputs, setValueInputs] = useState({});
  const [generatingDesc, setGeneratingDesc] = useState(false);
  const [activeSection, setActiveSection] = useState('basic');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data } = await categoriesAPI.getAll();
        setCategories(data);
      } catch (err) {
        setError('Failed to load categories');
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, []);

  const addGroup = () => {
    if (!newGroupName.trim()) {
      setError('Group name is required');
      return;
    }
    const clientId = `group-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const newGroup = {
      clientId,
      name: newGroupName.trim(),
      is_required: true,
      values: []
    };
    setFormData(prev => ({ ...prev, options: [...prev.options, newGroup] }));
    setValueInputs(prev => ({ ...prev, [clientId]: { name: '', price: '' } }));
    setNewGroupName('');
    setError('');
  };

  const toggleGroupRequired = (index) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.map((group, i) => i === index ? { ...group, is_required: !group.is_required } : group)
    }));
  };

  const deleteGroup = (index) => {
    setFormData(prev => {
      const groupToRemove = prev.options[index];
      const updatedOptions = prev.options.filter((_, i) => i !== index);
      setValueInputs(prevInputs => {
        if (!groupToRemove?.clientId) return prevInputs;
        const next = { ...prevInputs };
        delete next[groupToRemove.clientId];
        return next;
      });
      return { ...prev, options: updatedOptions };
    });
  };

  const handleValueInputChange = (groupId, field, value) => {
    setValueInputs(prev => ({
      ...prev,
      [groupId]: { ...(prev[groupId] || { name: '', price: '' }), [field]: value }
    }));
    setError('');
  };

  const addValueToGroup = (groupIndex, groupId) => {
    const inputState = valueInputs[groupId] || { name: '', price: '' };
    if (!inputState.name.trim() || inputState.price === '') {
      setError('Value name and extra price are required');
      return;
    }
    const parsedPrice = parseFloat(inputState.price);
    if (Number.isNaN(parsedPrice)) {
      setError('Please enter a valid price');
      return;
    }
    const newValue = {
      value: inputState.name.trim(),
      extra_price: parsedPrice
    };
    setFormData(prev => ({
      ...prev,
      options: prev.options.map((group, i) => 
        i === groupIndex 
          ? { ...group, values: [...group.values, newValue] }
          : group
      )
    }));
    setValueInputs(prev => ({ ...prev, [groupId]: { name: '', price: '' } }));
    setError('');
  };

  const deleteValue = (groupIndex, valueIndex) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.map((group, i) => 
        i === groupIndex
          ? { ...group, values: group.values.filter((_, j) => j !== valueIndex) }
          : group
      )
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Main image must be less than 5MB');
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const handleGalleryChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + galleryPreviews.length > 10) {
      setError('Maximum 10 gallery images allowed');
      return;
    }
    
    const validFiles = files.filter(file => {
      if (file.size > 5 * 1024 * 1024) {
        setError('Each gallery image must be less than 5MB');
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    setGalleryFiles([...galleryFiles, ...validFiles]);
    const previews = validFiles.map(file => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      return new Promise(resolve => {
        reader.onloadend = () => resolve(reader.result);
      });
    });
    Promise.all(previews).then(results => {
      setGalleryPreviews([...galleryPreviews, ...results]);
    });
    setError('');
  };

  const removeGalleryImage = (index) => {
    setGalleryFiles(galleryFiles.filter((_, i) => i !== index));
    setGalleryPreviews(galleryPreviews.filter((_, i) => i !== index));
  };

  const removeMainImage = () => {
    setImageFile(null);
    setImagePreview('');
  };

  const getDataUrl = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!formData.name.trim() || !formData.regularPrice || !formData.categoryId || !formData.stock || !formData.lowStockThreshold) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.shortDescription.trim().length > 0 && formData.shortDescription.trim().length < SHORT_DESC_MIN) {
      setError(`Short description must be at least ${SHORT_DESC_MIN} characters.`);
      return;
    }

    if (formData.longDescription.trim().length > 0 && formData.longDescription.trim().length < LONG_DESC_MIN) {
      setError(`Long description must be at least ${LONG_DESC_MIN} characters.`);
      return;
    }

    if (formData.salePrice && parseFloat(formData.salePrice) >= parseFloat(formData.regularPrice)) {
      setError('Sale price must be less than regular price');
      return;
    }

    for (const group of formData.options) {
      if (group.values.length === 0) {
        setError('Each option group must have at least one value');
        return;
      }
    }

    const selectedCategory = categories.find(cat => String(cat.id) === String(formData.categoryId));
    const seoTitle = (formData.seo_title || formData.name).trim() ? `${(formData.seo_title || formData.name).trim()}${formData.seo_title ? '' : ' | Store'}` : 'Store';
    const seoDescription = (formData.seo_description || formData.shortDescription || `Buy ${formData.name} at the best price`).trim().slice(0, 180);
    const seoKeywords = (formData.seo_keywords?.trim() || `${formData.name}${selectedCategory ? `, ${selectedCategory.name}` : ''}, online shopping`).trim();

    setLoading(true);
    
    try {
      let imageDataUrl = null;
      if (imageFile) {
        imageDataUrl = await getDataUrl(imageFile);
      }
      let galleryDataUrls = [];
      if (galleryFiles.length > 0) {
        galleryDataUrls = await Promise.all(galleryFiles.map(file => getDataUrl(file)));
      }
      const sanitizedOptions = formData.options.map(({ name, is_required, values }) => ({
        name,
        is_required,
        values
      }));
      const payload = {
        ...formData,
        regularPrice: parseFloat(formData.regularPrice),
        salePrice: formData.salePrice ? parseFloat(formData.salePrice) : null,
        categoryId: parseInt(formData.categoryId),
        stock: parseInt(formData.stock),
        lowStockThreshold: parseInt(formData.lowStockThreshold),
        shortDescription: formData.shortDescription.trim() || null,
        longDescription: formData.longDescription.trim().slice(0, LONG_DESC_MAX) || null,
        image: imageDataUrl,
        galleryImages: galleryDataUrls,
        options: sanitizedOptions,
        seo_title: seoTitle,
        seo_description: seoDescription,
        seo_keywords: seoKeywords
      };
      await productsAPI.create(payload);
      setSuccess(true);
      setTimeout(() => {
        navigate('/admin/products');
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add product. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleBack = () => {
    navigate('/admin/products');
  };

  const handleGenerateDescriptions = async () => {
    setError('');
    if (!formData.name.trim() || !formData.categoryId) {
      setError('Please enter name and select a category before generating descriptions.');
      return;
    }
    const selectedCategory = categories.find(cat => String(cat.id) === String(formData.categoryId));
    setGeneratingDesc(true);
    try {
      const { data } = await productsAPI.generateDescriptions({
        name: formData.name,
        categoryName: selectedCategory?.name,
        keywords: formData.seo_keywords || ''
      });
      setFormData(prev => ({
        ...prev,
        shortDescription: (data.shortDescription || prev.shortDescription || '').slice(0, 180),
        longDescription: (data.longDescription || prev.longDescription || '').slice(0, LONG_DESC_MAX)
      }));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate descriptions.');
    } finally {
      setGeneratingDesc(false);
    }
  };

  const sections = [
    { id: 'basic', label: 'Basic Info', icon: Package },
    { id: 'pricing', label: 'Pricing & Stock', icon: DollarSign },
    { id: 'description', label: 'Descriptions', icon: Tag },
    { id: 'options', label: 'Options', icon: Settings },
    { id: 'images', label: 'Images', icon: ImageIcon },
    { id: 'seo', label: 'SEO', icon: Search },
  ];

  if (loading) {
    return (
      <div className="product-loading-screen">
        <div className="loading-content">
          <div className="loading-spinner-large">
            <Loader2 size={48} strokeWidth={1.5} />
          </div>
          <h2>Creating Your Product</h2>
          <p>Please wait while we process your request...</p>
          <div className="loading-progress">
            <div className="progress-bar"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="product-page">
      {/* Top Navigation */}
      <div className="product-topbar">
        <div className="topbar-container">
          <div className="topbar-left">
            <button className="topbar-back" onClick={handleBack}>
              <ArrowLeft size={20} strokeWidth={2} />
            </button>
            <div className="topbar-title">
              <h1>Add New Product</h1>
              <span className="topbar-badge">Draft</span>
            </div>
          </div>
          <div className="topbar-right">
            <button className="topbar-btn topbar-btn-secondary" onClick={handleBack}>
              Cancel
            </button>
            <button 
              className="topbar-btn topbar-btn-primary" 
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check size={18} strokeWidth={2.5} />
                  Publish Product
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="product-layout">
        {/* Sidebar Navigation */}
        <aside className="product-sidebar">
          <nav className="sidebar-nav">
            {sections.map((section) => {
              const IconComponent = section.icon;
              return (
                <button
                  key={section.id}
                  className={`sidebar-nav-item ${activeSection === section.id ? 'active' : ''}`}
                  onClick={() => setActiveSection(section.id)}
                >
                  <IconComponent size={20} strokeWidth={1.8} />
                  <span>{section.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Form Content */}
        <main className="product-main">
          {/* Alert Messages */}
          {error && (
            <div className="alert alert-error">
              <AlertCircle size={22} strokeWidth={2} />
              <div className="alert-content">
                <strong>Error</strong>
                <span>{error}</span>
              </div>
              <button className="alert-close" onClick={() => setError('')}>
                <X size={18} />
              </button>
            </div>
          )}
          
          {success && (
            <div className="alert alert-success">
              <Check size={22} strokeWidth={2} />
              <div className="alert-content">
                <strong>Success!</strong>
                <span>Product created successfully. Redirecting...</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="product-form">
            {/* Basic Information Section */}
            <section className={`form-section ${activeSection === 'basic' ? 'active' : ''}`} id="basic">
              <div className="section-header">
                <div className="section-icon">
                  <Package size={22} strokeWidth={1.8} />
                </div>
                <div className="section-title">
                  <h2>Basic Information</h2>
                  <p>Essential details about your product</p>
                </div>
              </div>

              <div className="section-content">
                <div className="form-row">
                  <div className="form-group form-group-full">
                    <label className="form-label">
                      Product Name
                      <span className="required-indicator">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="form-input form-input-lg"
                      placeholder="Enter product name"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      Category
                      <span className="required-indicator">*</span>
                    </label>
                    {loadingCategories ? (
                      <div className="skeleton-input"></div>
                    ) : (
                      <div className="select-wrapper">
                        <select
                          name="categoryId"
                          value={formData.categoryId}
                          onChange={handleChange}
                          className="form-select"
                          required
                        >
                          <option value="">Select a category</option>
                          {categories.map(category => (
                            <option key={category.id} value={category.id}>{category.name}</option>
                          ))}
                        </select>
                        <ChevronDown size={18} className="select-icon" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {/* Pricing & Stock Section */}
            <section className={`form-section ${activeSection === 'pricing' ? 'active' : ''}`} id="pricing">
              <div className="section-header">
                <div className="section-icon">
                  <DollarSign size={22} strokeWidth={1.8} />
                </div>
                <div className="section-title">
                  <h2>Pricing & Stock</h2>
                  <p>Set your product pricing and inventory</p>
                </div>
              </div>

              <div className="section-content">
                <div className="form-grid form-grid-2">
                  <div className="form-group">
                    <label className="form-label">
                      Regular Price (DT)
                      <span className="required-indicator">*</span>
                    </label>
                    <div className="input-with-icon">
                      <DollarSign size={18} className="input-icon" />
                      <input
                        type="number"
                        name="regularPrice"
                        value={formData.regularPrice}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Sale Price (DT)
                      <span className="optional-tag">Optional</span>
                    </label>
                    <div className="input-with-icon">
                      <DollarSign size={18} className="input-icon" />
                      <input
                        type="number"
                        name="salePrice"
                        value={formData.salePrice}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                      />
                    </div>
                    {formData.salePrice && formData.regularPrice && (
                      <div className="discount-badge">
                        {Math.round((1 - parseFloat(formData.salePrice) / parseFloat(formData.regularPrice)) * 100)}% OFF
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Stock Quantity
                      <span className="required-indicator">*</span>
                    </label>
                    <div className="input-with-icon">
                      <Box size={18} className="input-icon" />
                      <input
                        type="number"
                        name="stock"
                        value={formData.stock}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="0"
                        min="0"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Low Stock Alert
                      <span className="required-indicator">*</span>
                    </label>
                    <div className="input-with-icon">
                      <AlertCircle size={18} className="input-icon" />
                      <input
                        type="number"
                        name="lowStockThreshold"
                        value={formData.lowStockThreshold}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="10"
                        min="0"
                        required
                      />
                    </div>
                    <span className="input-hint">Alert when stock falls below this number</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Descriptions Section */}
            <section className={`form-section ${activeSection === 'description' ? 'active' : ''}`} id="description">
              <div className="section-header">
                <div className="section-icon">
                  <Tag size={22} strokeWidth={1.8} />
                </div>
                <div className="section-title">
                  <h2>Product Descriptions</h2>
                  <p>Describe your product in detail</p>
                </div>
                <button
                  type="button"
                  className="ai-generate-btn"
                  onClick={handleGenerateDescriptions}
                  disabled={generatingDesc}
                >
                  {generatingDesc ? (
                    <>
                      <Loader2 size={16} className="spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles size={16} />
                      AI Generate
                    </>
                  )}
                </button>
              </div>

              <div className="section-content">
                <div className="form-group">
                  <label className="form-label">
                    Short Description
                    <span className="optional-tag">Optional</span>
                  </label>
                  <textarea
                    name="shortDescription"
                    value={formData.shortDescription}
                    onChange={handleChange}
                    className="form-textarea"
                    placeholder="A brief, engaging summary of your product..."
                    rows={3}
                    maxLength={180}
                  />
                  <div className="textarea-footer">
                    <span className="input-hint">Appears in product listings</span>
                    <span className="char-counter">{formData.shortDescription.length}/180</span>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Long Description
                    <span className="optional-tag">Optional</span>
                  </label>
                  <textarea
                    name="longDescription"
                    value={formData.longDescription}
                    onChange={handleChange}
                    className="form-textarea form-textarea-lg"
                    placeholder="Detailed product information, features, specifications, materials, dimensions..."
                    rows={8}
                    maxLength={LONG_DESC_MAX}
                  />
                  <div className="textarea-footer">
                    <span className="input-hint">Full product details for the product page</span>
                    <span className="char-counter">{formData.longDescription.length}/{LONG_DESC_MAX}</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Product Options Section */}
            <section className={`form-section ${activeSection === 'options' ? 'active' : ''}`} id="options">
              <div className="section-header">
                <div className="section-icon">
                  <Settings size={22} strokeWidth={1.8} />
                </div>
                <div className="section-title">
                  <h2>Product Options</h2>
                  <p>Add variants like size, color, or custom options</p>
                </div>
              </div>

              <div className="section-content">
                {formData.options.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">
                      <Settings size={40} strokeWidth={1.2} />
                    </div>
                    <h3>No Options Added</h3>
                    <p>Add option groups to let customers customize their purchase</p>
                  </div>
                ) : (
                  <div className="options-list">
                    {formData.options.map((group, groupIndex) => {
                      const valueInputKey = group.clientId || `group-${groupIndex}`;
                      const currentValueInput = valueInputs[valueInputKey] || { name: '', price: '' };
                      return (
                        <div key={group.clientId || groupIndex} className="option-group-card">
                          <div className="option-group-header">
                            <div className="option-group-title">
                              <GripVertical size={18} className="drag-handle" />
                              <h4>{group.name}</h4>
                            </div>
                            <div className="option-group-actions">
                              <label className="toggle-switch">
                                <input
                                  type="checkbox"
                                  checked={group.is_required}
                                  onChange={() => toggleGroupRequired(groupIndex)}
                                />
                                <span className="toggle-slider"></span>
                                <span className="toggle-label">Required</span>
                              </label>
                              <button
                                type="button"
                                onClick={() => deleteGroup(groupIndex)}
                                className="delete-group-btn"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>

                          <div className="option-values">
                            {group.values.length === 0 ? (
                              <p className="no-values-text">No values added yet</p>
                            ) : (
                              <div className="values-grid">
                                {group.values.map((value, valueIndex) => (
                                  <div key={valueIndex} className="value-chip">
                                    <span className="value-name">{value.value}</span>
                                    <span className="value-price">+{value.extra_price.toFixed(2)} DT</span>
                                    <button
                                      type="button"
                                      onClick={() => deleteValue(groupIndex, valueIndex)}
                                      className="remove-value-btn"
                                    >
                                      <X size={14} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="add-value-form">
                            <input
                              type="text"
                              placeholder="Value name (e.g., Red, Large)"
                              value={currentValueInput.name}
                              onChange={(e) => handleValueInputChange(valueInputKey, 'name', e.target.value)}
                              className="form-input form-input-sm"
                            />
                            <input
                              type="number"
                              placeholder="Extra price"
                              value={currentValueInput.price}
                              onChange={(e) => handleValueInputChange(valueInputKey, 'price', e.target.value)}
                              className="form-input form-input-sm"
                              step="0.01"
                            />
                            <button
                              type="button"
                              onClick={() => addValueToGroup(groupIndex, valueInputKey)}
                              className="add-value-btn"
                            >
                              <Plus size={16} />
                              Add
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="add-group-form">
                  <input
                    type="text"
                    placeholder="Option group name (e.g., Color, Size, Material)"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    className="form-input"
                  />
                  <button type="button" onClick={addGroup} className="add-group-btn">
                    <Plus size={18} />
                    Add Option Group
                  </button>
                </div>
              </div>
            </section>

            {/* Images Section */}
            <section className={`form-section ${activeSection === 'images' ? 'active' : ''}`} id="images">
              <div className="section-header">
                <div className="section-icon">
                  <ImageIcon size={22} strokeWidth={1.8} />
                </div>
                <div className="section-title">
                  <h2>Product Images</h2>
                  <p>Upload high-quality images of your product</p>
                </div>
              </div>

              <div className="section-content">
                {/* Main Image */}
                <div className="form-group">
                  <label className="form-label">
                    Main Image
                    <span className="optional-tag">Recommended</span>
                  </label>
                  
                  {imagePreview ? (
                    <div className="main-image-preview">
                      <img src={imagePreview} alt="Product preview" />
                      <div className="image-overlay">
                        <button
                          type="button"
                          onClick={removeMainImage}
                          className="remove-image-btn"
                        >
                          <Trash2 size={18} />
                          Remove
                        </button>
                      </div>
                      <div className="image-badge">
                        <Check size={14} />
                        Main Image
                      </div>
                    </div>
                  ) : (
                    <label className="upload-area">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="file-input-hidden"
                      />
                      <div className="upload-content">
                        <div className="upload-icon">
                          <Upload size={32} strokeWidth={1.5} />
                        </div>
                        <div className="upload-text">
                          <span className="upload-primary">Click to upload main image</span>
                          <span className="upload-secondary">PNG, JPG up to 5MB</span>
                        </div>
                      </div>
                    </label>
                  )}
                </div>

                {/* Gallery Images */}
                <div className="form-group">
                  <label className="form-label">
                    Gallery Images
                    <span className="optional-tag">Up to 10 images</span>
                  </label>
                  
                  <div className="gallery-grid">
                    {galleryPreviews.map((preview, index) => (
                      <div key={index} className="gallery-item">
                        <img src={preview} alt={`Gallery ${index + 1}`} />
                        <button
                          type="button"
                          onClick={() => removeGalleryImage(index)}
                          className="gallery-remove-btn"
                        >
                          <X size={14} />
                        </button>
                        <span className="gallery-number">{index + 1}</span>
                      </div>
                    ))}
                    
                    {galleryPreviews.length < 10 && (
                      <label className="gallery-upload-slot">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleGalleryChange}
                          className="file-input-hidden"
                        />
                        <Plus size={24} />
                        <span>Add Image</span>
                      </label>
                    )}
                  </div>
                  
                  {galleryPreviews.length > 0 && (
                    <p className="gallery-counter">
                      <Images size={16} />
                      {galleryPreviews.length} of 10 images uploaded
                    </p>
                  )}
                </div>
              </div>
            </section>

            {/* SEO Section */}
            <section className={`form-section ${activeSection === 'seo' ? 'active' : ''}`} id="seo">
              <div className="section-header">
                <div className="section-icon">
                  <Search size={22} strokeWidth={1.8} />
                </div>
                <div className="section-title">
                  <h2>SEO Settings</h2>
                  <p>Optimize for search engines (auto-generated if left blank)</p>
                </div>
              </div>

              <div className="section-content">
                <div className="form-group">
                  <label className="form-label">
                    SEO Title
                    <span className="optional-tag">Optional</span>
                  </label>
                  <input
                    type="text"
                    name="seo_title"
                    value={formData.seo_title}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Product name | Your Store"
                    maxLength={70}
                  />
                  <span className="input-hint">Appears in browser tabs and search results</span>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    SEO Keywords
                    <span className="optional-tag">Optional</span>
                  </label>
                  <input
                    type="text"
                    name="seo_keywords"
                    value={formData.seo_keywords}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="keyword1, keyword2, keyword3"
                  />
                  <span className="input-hint">Comma-separated keywords</span>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    SEO Description
                    <span className="optional-tag">Optional</span>
                  </label>
                  <textarea
                    name="seo_description"
                    value={formData.seo_description}
                    onChange={handleChange}
                    className="form-textarea"
                    placeholder="A brief description that appears in search results..."
                    rows={3}
                    maxLength={180}
                  />
                  <div className="textarea-footer">
                    <span className="input-hint">Appears under the title in search results</span>
                    <span className="char-counter">{formData.seo_description.length}/180</span>
                  </div>
                </div>

                {/* SEO Preview */}
                <div className="seo-preview">
                  <h4>Search Engine Preview</h4>
                  <div className="seo-preview-content">
                    <div className="seo-preview-title">
                      {formData.seo_title || formData.name || 'Product Title'} | Store
                    </div>
                    <div className="seo-preview-url">
                      www.yourstore.com/product/{formData.name?.toLowerCase().replace(/\s+/g, '-') || 'product-name'}
                    </div>
                    <div className="seo-preview-desc">
                      {formData.seo_description || formData.shortDescription || 'Product description will appear here...'}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </form>
        </main>
      </div>

      {/* Mobile Bottom Actions */}
      <div className="mobile-actions">
        <button className="mobile-btn mobile-btn-secondary" onClick={handleBack}>
          Cancel
        </button>
        <button 
          className="mobile-btn mobile-btn-primary" 
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Publish'}
        </button>
      </div>
    </div>
  );
};

export default AddProduct;